// src/components/dashboard/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  DocumentData
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EventType } from '../../types/Event';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkHeading from '../common/SparkHeading';

interface EventWithStats extends EventType {
  stats?: {
    accepted: number;
    declined: number;
    viewed: number;
    sent: number;
  };
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<EventWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const unsubscribes: (() => void)[] = [];

    const fetchEvents = async () => {
      try {
        const eventsQuery = query(
          collection(db, 'events'),
          where('userId', '==', user.uid),
          orderBy('eventDate', 'desc')
        );
        const snapshot = await getDocs(eventsQuery);

        const list = snapshot.docs.map(doc => ({
          id: doc.id,
          ...(doc.data() as Omit<EventType, 'id'>),
          stats: { accepted: 0, declined: 0, viewed: 0, sent: 0 }
        })) as EventWithStats[];

        setEvents(list);

        list.forEach(event => {
          if (!event.id) return;

          const statsQuery = query(
            collection(db, 'invitations'),
            where('eventId', '==', event.id)
          );
          const unsubscribe = onSnapshot(
            statsQuery,
            snapshot => {
              let accepted = 0, declined = 0, viewed = 0, sent = 0;

              snapshot.forEach(doc => {
                const status = (doc.data() as DocumentData).status || 'sent';
                if (status === 'accepted') accepted++;
                else if (status === 'declined') declined++;
                else if (status === 'viewed') viewed++;
                else sent++;
              });

              setEvents(prev =>
                prev.map(ev =>
                  ev.id === event.id
                    ? { ...ev, stats: { accepted, declined, viewed, sent } }
                    : ev
                )
              );
            },
            err => {
              console.error(`Error listening to stats for ${event.id}:`, err);
            }
          );

          unsubscribes.push(unsubscribe);
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to load events. Please try again.');
        setLoading(false);
      }
    };

    fetchEvents();

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [user]);

  const handleRowClick = (eventId?: string) => {
    if (eventId) navigate(`/event/${eventId}`);
  };

  if (loading) {
    return (
      <SparkLayout>
        <SparkCard>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              color: 'var(--color-text-primary)',
            }}
          >
            <div className="spark-spinner" />
            <span style={{ marginLeft: '1rem' }}>Loading your events...</span>
          </div>
        </SparkCard>
      </SparkLayout>
    );
  }

  if (error) {
    return (
      <SparkLayout>
        <SparkCard>
          <div className="spark-message spark-message--error">{error}</div>
        </SparkCard>
      </SparkLayout>
    );
  }

  return (
    <SparkLayout>
      {/* Create Event buttons */}
      <div style={{ 
        marginTop: '2rem', 
        marginBottom: '1.5rem',
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <SparkButton to="/create-event" size="large">
          CREATE NEW EVENT
        </SparkButton>
        <SparkButton to="/create-event-from-template" size="large">
          SEE ALL INVITATIONS
        </SparkButton>
      </div>

      <div
        style={{
          width: '83.333%',
          maxWidth: '80rem',
          marginBottom: '4rem',
          overflow: 'auto',
          maxHeight: '70vh'
        }}
      >
        <SparkHeading level="h1" glow>
          Your Events
        </SparkHeading>

        {events.length === 0 ? (
          <div className="spark-card spark-card--solid" style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#000' }}>
              You haven't created any events yet.
            </p>
            <SparkButton to="/create-event" variant="violet">
              CREATE YOUR FIRST EVENT
            </SparkButton>
          </div>
        ) : (
          <div style={{ width: '100%', marginBottom: '1.5rem' }}>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="spark-table" style={{ minWidth: '650px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Event Name</th>
                    <th style={{ width: '25%', textAlign: 'center' }}>Date</th>
                    <th style={{ width: '17.5%', textAlign: 'center' }}>Accepted</th>
                    <th style={{ width: '17.5%', textAlign: 'center' }}>Declined</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map(ev => {
                    const dateStr = ev.eventDate?.toDate
                      ? ev.eventDate.toDate().toLocaleDateString()
                      : 'No date';

                    return (
                      <tr
                        key={ev.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(ev.id)}
                      >
                        <td className="spark-table__cell--title">{ev.title}</td>
                        <td style={{ textAlign: 'center' }}>{dateStr}</td>
                        <td
                          style={{
                            textAlign: 'center',
                            color: 'var(--color-success)',
                            fontWeight: 'bold',
                          }}
                        >
                          {ev.stats?.accepted ?? 0}
                        </td>
                        <td
                          style={{ textAlign: 'center', fontWeight: 'bold' }}
                        >
                          {ev.stats?.declined ?? 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </SparkLayout>
  );
};

export default Dashboard;
